/* eslint-disable prefer-spread */
/* eslint-disable no-use-before-define */
/* eslint-disable no-console */
/**
 * @module BrowserLogger
 */
const { toType } = require('./toType');

/**
 * @typedef ConfigOptions
 * @property {boolean} colorize - Boolean flag denoting whether or not to color the console output.
 * @property {boolean} includeTimestamp - Boolean flag denoting whether or not to add ISO-formatted timestamp in parenthesis at the end of the console output.
 * @property {string} label - The label with which to attribute to and prefix log messages.
 * @property {boolean} printOutput - Boolean flag denoting whether or not to print console output.
 */

/**
 * @typedef ErrorLevel
 * @property {string} color - The color value.
 * @property {string} label - The label for the log message.
 */

/**
 * Utility to configure and output messages to the browser console.
 *
 * @alias module:browserLogger
 *
 * @example
 * // Configuration.
 * Log.configure({
 *   colorize: false,
 *   includeTimestamp: true,
 *   label: '[Sample Label]',
 *   printOutput: true,
 * });
 * // Usage.
 * Log.log('Sample log message.');
 * Log.error(new Error('Sample error message.'));
 */
const Log = {
  configure,
  error,
  info,
  log,
  options,
  success,
  warn,
};

const CONFIG = {
  defaultOptions: {
    colorize: false,
    includeTimestamp: false,
    label: '',
    printOutput: true,
  },
  levels: {
    error: {
      color: 'red',
      label: 'error',
    },
    info: {
      color: 'blue',
      label: 'info',
    },
    log: {
      color: 'charcoal',
      label: 'log',
    },
    success: {
      color: 'green',
      label: 'success',
    },
    warn: {
      color: 'orange',
      label: 'warn',
    },
  },
  localStorageKeys: {
    colorize: 'LC:ION:BrowserLogger:Colorize',
    includeTimestamp: 'LC:ION:BrowserLogger:IncludeTimestamp',
    label: 'LC:ION:BrowserLogger:Label',
    printOutput: 'LC:ION:BrowserLogger:PrintOutput',
  },
};

/**
 * Format and return a list of args from the specified parameters.
 *
 * @param {ErrorLevel} level - The ErrorLevel object from `CONFIG` to use for formatting.
 * @param {(string|object)} message - String or object to output.
 * @param  {...any} params - Optional list of objects to output.
 *
 * @returns {Array} List of formatted args.
 */
function formatLogArgs(level, message, ...params) {
  const config = Log.options();

  // If message is string, we want to output it as such. Otherwise, set arg1
  // to blank string and immediately add to start of returned args. This is
  // necessary for properly outputting objects as the `message` parameter.
  const arg1 = toType(message) === 'string' ? ` ${message}` : '';
  const argN = Array.prototype.slice.call(...params);
  /* istanbul ignore next */
  let label = '';
  /* istanbul ignore next */
  if (config.label) {
    label = `[${config.label}]`;
  }
  /* istanbul ignore next */
  const timestamp = config.includeTimestamp
    ? ` (${new Date().toISOString()})`
    : '';
  if (toType(message) !== 'string') {
    argN.unshift(message);
  }

  // Add arg1 and label to the start of returned args, wrapping with
  // appropriate strings and styles if `config.colorize` is `true`.
  /* istanbul ignore next */
  if (config.colorize.toString() === 'true') {
    const colorize = config.colorize ? '%c' : '';
    argN.unshift(
      `${colorize}${label}${arg1}${timestamp}`,
      `color: ${level.color};`,
    );
  } else {
    /* istanbul ignore next */
    argN.unshift(`${label}${arg1}${timestamp}`);
  }
  return argN;
}

/**
 * Retrieve the configuration options for the Log utility.
 *
 * @returns {ConfigOptions} ConfigOptions object for the Log utility.
 */
function options() {
  /* istanbul ignore next */
  const storedColorizeKey =
    window && window.localStorage
      ? window.localStorage.getItem(CONFIG.localStorageKeys.colorize)
      : CONFIG.defaultOptions.colorize;
  /* istanbul ignore next */
  const storedIncludeTimestampKey =
    window && window.localStorage
      ? window.localStorage.getItem(CONFIG.localStorageKeys.includeTimestamp)
      : CONFIG.defaultOptions.includeTimestamp;
  /* istanbul ignore next */
  const storedLabelKey =
    window && window.localStorage
      ? window.localStorage.getItem(CONFIG.localStorageKeys.label)
      : CONFIG.defaultOptions.label;
  /* istanbul ignore next */
  const storedPrintOutputKey =
    window && window.localStorage
      ? window.localStorage.getItem(CONFIG.localStorageKeys.printOutput)
      : CONFIG.defaultOptions.printOutput;

  /* istanbul ignore next */
  return {
    colorize: storedColorizeKey
      ? storedColorizeKey.toString() === 'true'
      : CONFIG.defaultOptions.colorize,
    includeTimestamp: storedIncludeTimestampKey
      ? storedIncludeTimestampKey.toString() === 'true'
      : CONFIG.defaultOptions.includeTimestamp,
    label: storedLabelKey
      ? storedLabelKey.toString()
      : CONFIG.defaultOptions.label,
    printOutput: storedPrintOutputKey
      ? storedPrintOutputKey.toString() === 'true'
      : CONFIG.defaultOptions.printOutput,
  };
}

/**
 * Output the specified error message to the console.
 *
 * @param {(string|object)} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
function error(message, ...params) {
  try {
    const config = Log.options();
    /* istanbul ignore next */
    if (config.printOutput) {
      const args = formatLogArgs(CONFIG.levels.error, message, params);
      console.error.apply(console, args);
    }
  } catch (e) {
    // Silent failure is acceptable.
  }
}

/**
 * Output the specified info message to the console.
 *
 * @param {(string|object)} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
function info(message, ...params) {
  try {
    const config = Log.options();
    /* istanbul ignore next */
    if (config.printOutput) {
      const args = formatLogArgs(CONFIG.levels.info, message, params);
      console.info.apply(console, args);
    }
  } catch (e) {
    // Silent failure is acceptable.
  }
}

/**
 * Output the specified log message to the console.
 *
 * @param {(string|object)} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
function log(message, ...params) {
  try {
    const config = Log.options();
    /* istanbul ignore next */
    if (config.printOutput) {
      const args = formatLogArgs(CONFIG.levels.log, message, params);
      console.log.apply(console, args);
    }
  } catch (e) {
    // Silent failure is acceptable.
  }
}

/**
 * Output the specified success message to the console.
 *
 * @param {(string|object)} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
function success(message, ...params) {
  try {
    const config = Log.options();
    /* istanbul ignore next */
    if (config.printOutput) {
      const args = formatLogArgs(CONFIG.levels.success, message, params);
      console.log.apply(console, args);
    }
  } catch (e) {
    // Silent failure is acceptable.
  }
}

/**
 * Output the specified warn message to the console.
 *
 * @param {(string|object)} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
function warn(message, ...params) {
  try {
    const config = Log.options();
    /* istanbul ignore next */
    if (config.printOutput) {
      const args = formatLogArgs(CONFIG.levels.warn, message, params);
      console.warn.apply(console, args);
    }
  } catch (e) {
    // Silent failure is acceptable.
  }
}

/**
 * Configure the Log utility with the specified options.
 *
 * @param {object} [configOptions] - The object of configuration options.
 * @param {boolean} [configOptions.colorize] - If true, will colorize the message outputs.
 * @param {boolean} [configOptions.includeTimestamp] - If true, will add the ISO-formatted timestamp in parenthesis to the end of the message outputs.
 * @param {string} [configOptions.label] - A label to prepend to the message outputs.
 * @param {boolean} [configOptions.printOutput] - If true, will print the message to the console.
 *
 * @example
 * // Will only print output to console when not production environment.
 * Log.configure({ colorize: true, includeTimestamp: false, label: '[EXAMPLE]:', printOutput: process.env.NODE_ENV !== 'production' })
 */
function configure(configOptions = CONFIG.defaultOptions) {
  try {
    /* istanbul ignore next */
    if (window && window.localStorage) {
      window.localStorage.setItem(
        CONFIG.localStorageKeys.colorize,
        configOptions.colorize,
      );
      window.localStorage.setItem(
        CONFIG.localStorageKeys.includeTimestamp,
        configOptions.includeTimestamp,
      );
      window.localStorage.setItem(
        CONFIG.localStorageKeys.label,
        configOptions.label || CONFIG.defaultOptions.label,
      );
      window.localStorage.setItem(
        CONFIG.localStorageKeys.printOutput,
        configOptions.printOutput,
      );
      /* istanbul ignore next */
      if (configOptions.printOutput) {
        this.info(`browserLogger configured!`);
        this.info(configOptions);
      }
    }
  } catch (e) {
    // Silent failure is acceptable.
  }
}

module.exports = {
  Log,
};
